import { Box } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ConsentTermActions from './ConsentTermActions';
import ConsentTermContent from './ConsentTermContent';
import {
  consentAppSelectors,
  consentFormSuccess,
} from 'redux/slices/consent.slice';
import ConsentTermHeader from './ConsentTermHeader';
import ConsentIdentityStepper, {
  ConsentIdentityStepperRef,
} from './ConsentIdentityStepper';
import { useState, useRef, useEffect } from 'react';
import { STEPPER_SECTIONS } from './stepperSections';
import { useApiCall } from 'redux/utils/api-hook';
import { consentRequests } from 'redux/requests/consents.requests';
import { useAppDispatch } from 'redux/store-hooks';

interface ConsentTermProps {
  termKey?: string;
}

const ConsentTerm = ({ termKey }: ConsentTermProps) => {
  const [params] = useSearchParams();
  const key = params.get('key');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const terms = consentAppSelectors.useSelectAllTerms();
  const term = terms.find(term => term.key === termKey);
  const consentState = consentAppSelectors.useSelectConsentState();

  const showIdentityForm =
    term?.identityRequired && !consentState.termIdentityId;

  const [identityFormStep, setIdentityFormStep] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [consented, setConsented] = useState<boolean | null>(null);
  const [identitySubmissionId, setIdentitySubmissionId] = useState<
    string | null
  >(null);
  const stepperRef = useRef<ConsentIdentityStepperRef>(null);
  const identityIdRef = useRef<string | null>(null);

  const [
    consentFormResponse,
    isSubmittingConsent,
    submitConsentForm,
    _consentFormErrors,
  ] = useApiCall(consentRequests.postTermForm);

  useEffect(() => {
    if (consentFormResponse && consented !== null) {
      navigate(`/?key=${key}`);
    }
  }, [consentFormResponse, consented, navigate, key]);

  useEffect(() => {
    if (consentState.termIdentityId && !identitySubmissionId) {
      setIdentitySubmissionId(consentState.termIdentityId);
      identityIdRef.current = consentState.termIdentityId;
    }
  }, [consentState.termIdentityId, identitySubmissionId]);

  useEffect(() => {
    if (consentFormResponse) {
      dispatch(consentFormSuccess(consentFormResponse));
    }
  }, [consentFormResponse, dispatch]);

  const handleFormSubmit = async () => {
    if (identityFormStep === 1 && stepperRef.current) {
      try {
        await stepperRef.current.submitForm();

        const waitForIdentityId = () => {
          if (identityIdRef.current) {
            setIdentityFormStep(2);
          } else {
            setTimeout(waitForIdentityId, 100);
          }
        };

        waitForIdentityId();
      } catch (error) {
        console.error('Form submission failed:', error);
      }
    }
  };

  const handleConsentSubmit = async (consentValue: boolean) => {
    if (!term) {
      return;
    }

    setConsented(consentValue);

    if (!term.identityRequired) {
      try {
        await submitConsentForm({
          body: {
            consented: consentValue,
            termId: term.id,
            consentIdentityId: undefined, // No identity required
            consentLinkKey: key,
          },
          params: null,
        });
        return;
      } catch (error) {
        console.error('Consent submission failed:', error);
        return;
      }
    }

    const consentIdentityId =
      identitySubmissionId || consentState.termIdentityId;
    if (!consentIdentityId) {
      console.error(
        'No identity submission ID available for consent submission'
      );
      return;
    }

    try {
      await submitConsentForm({
        body: {
          consented: consentValue,
          termId: term.id,
          consentIdentityId: consentIdentityId,
          consentLinkKey: key,
        },
        params: null,
      });
    } catch (error) {
      console.error('Consent submission failed:', error);
    }
  };

  const isLastStep = identityFormStep === STEPPER_SECTIONS.length - 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#f8f5f1',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          margin: '20px 20px 16px 20px',
        }}
      >
        <ConsentTermHeader term={term} />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '0 20px 20px',
          boxSizing: 'border-box',
        }}
      >
        {showIdentityForm ? (
          <ConsentIdentityStepper
            term={term}
            isLastStep={isLastStep}
            activeStep={identityFormStep}
            setActiveStep={setIdentityFormStep}
            onFormValidityChange={setIsFormValid}
            ref={stepperRef}
          />
        ) : (
          <ConsentTermContent term={term} />
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          flexGrow: 0,
          height: 'auto',
        }}
      >
        <ConsentTermActions
          showIdentityForm={showIdentityForm}
          isLastStep={isLastStep}
          activeStep={identityFormStep}
          setActiveStep={setIdentityFormStep}
          isFormValid={isFormValid}
          onFormSubmit={handleFormSubmit}
          onConsentSubmit={handleConsentSubmit}
          isSubmittingConsent={isSubmittingConsent}
          consented={consented}
        />
      </Box>
    </Box>
  );
};

export default ConsentTerm;
